/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import Markdown from 'markdown-to-jsx';

import { alignTypes, RequiredSpan } from '@web-packages/library/layouts';
import CustomImage from '../CustomImage';
import Textlink from '../Textlink';
import { types as customTextTypes } from '../CustomText';

import { Wrapper } from './styled';

import types from './types';
import variants from './variants';

// eslint-disable-next-line react/prop-types
const TextlinkMap = ({ href, customTextType, children }) => (
  <Textlink linkText={children[0]} url={href} name={href} customTextType={customTextType} />
);
// eslint-disable-next-line no-unused-vars
const CustomImageMap = ({ src, alt }) => (
  <CustomImage
    url={src}
    type="fill"
    objectFit="cover"
    minHeight={{
      phone: 300,
      tablet: 400,
      desktop: 500,
    }}
    alternativeText={alt}
  />
);
CustomImageMap.displayName = 'CustomImage';

const stripIndent = (str) => {
  try {
    return str.split('\n').map((l) => l.trim()).join('\n');
  } catch (_e) {
    return str;
  }
};

const hasImage = (child) => typeof child === 'object'
&& child?.type?.displayName === 'CustomImage';

const sanitizeHTML = (_html, showRequiredIcon, customTextType) => {
  const html = showRequiredIcon ? `${_html}<RequiredSpan>` : _html;

  return (
    <Markdown
      className="line-break"
      options={{
        overrides: {
          a: {
            component: (props) => <TextlinkMap {...props} customTextType={customTextType} />,
          },
          img: {
            component: CustomImageMap,
          },
          span: {
            component: ({ children }) => <p>{children}</p>,
          },
          p: {
            component: ({ children }) => {
              const hasImageChild = children?.find((child) => hasImage(child));

              let handleImage = null;
              if (hasImageChild) {
              // eslint-disable-next-line react/no-array-index-key
                handleImage = children?.map((child, index) => (hasImage(child) ? child : <p key={index}>{child}</p>));
              }

              return hasImageChild ? handleImage : <p>{children}</p>;
            },
          },
          RequiredSpan: {
            component: () => <RequiredSpan>*</RequiredSpan>,
          },
        },
      }}
    >
      {stripIndent(html)}
    </Markdown>
  );
};

const htmlHasImage = (_html) => /(?:!\[(.*?)\]\((.*?)\))/.test(_html) || _html.includes('img');

const RichText = ({
  variant,
  html,
  centerContent,
  customTextType,
  type,
  showRequiredIcon,

  innerRef,
}) => {
  if (html == null) return null;

  return (
    <Wrapper
      variant={variant}
      alignItems={centerContent ? alignTypes.center : undefined}
      customTextType={customTextType}
      type={type}
      isModalTypeAndImage={type === types.modalType && htmlHasImage(html)}
      ref={innerRef}
    >
      {sanitizeHTML(html, showRequiredIcon, customTextType)}
    </Wrapper>
  );
};

RichText.propTypes = {
  type: PropTypes.oneOf(Object.values(types)),
  variant: PropTypes.oneOf(Object.values(variants)),

  html: PropTypes.string.isRequired,
  customTextType: PropTypes.oneOf(Object.values(customTextTypes)),

  centerContent: PropTypes.bool,
  showRequiredIcon: PropTypes.bool,

  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Object) }),
  ]),
};

RichText.defaultProps = {
  type: types.defaultType,
  variant: variants.primary,

  customTextType: customTextTypes.textBasis,

  centerContent: false,
  showRequiredIcon: false,

  innerRef: null,
};

export default RichText;
